function renderCardComponentTabValuesTemplate({
  groupVarPrefix,
  map,
  toPascalCase
}: {
  groupVarPrefix: string
  map: string
  toPascalCase: string
}): string {
  return `
const ${groupVarPrefix}TabValues = [${map}] as const
const ${groupVarPrefix}TabParser = parseAsStringLiteral(${groupVarPrefix}TabValues).withDefault(${groupVarPrefix}TabValues[0])

type ${toPascalCase}TabValue = (typeof ${groupVarPrefix}TabValues)[number]
`
}

function renderCardComponentCardHeaderTemplate({
  groupVarPrefix,
  zodFields,
  tabParserConst,
  groupComponentName,
  singular,
  analysisHasTabsFieldTab,
  relStateContent,
  creatableSelectStateContent,
  selectStateContent,
  defaultValues,
  savedValueName,
  toCamelCase,
  resetValues,
  groupTitle,
  schemaName,
  defaultValueFromSyncFunctionsContent,
  fieldArrayHooksContent,
  creatableSelectHandlersContent,
  descriptionLine,
  fieldsJsx,
  groupHintText
}: {
  groupVarPrefix: string
  zodFields: string
  tabParserConst: string
  groupComponentName: string
  singular: string
  analysisHasTabsFieldTab: string
  relStateContent: string
  creatableSelectStateContent: string
  selectStateContent: string
  defaultValues: string
  savedValueName: string
  toCamelCase: string
  resetValues: string
  groupTitle: string
  schemaName: string
  defaultValueFromSyncFunctionsContent: string
  fieldArrayHooksContent: string
  creatableSelectHandlersContent: string
  descriptionLine: string
  fieldsJsx: string
  groupHintText: string
}): string {
  return `const ${groupVarPrefix}Schema = z.object({
${zodFields}
})
${tabParserConst}

export function ${groupComponentName}({ initialData }: { initialData?: ${singular} | null }) {
  const queryClient = useQueryClient()${analysisHasTabsFieldTab}${relStateContent}${creatableSelectStateContent}${selectStateContent}

  const form = useForm<z.infer<typeof ${groupVarPrefix}Schema>>({
    resolver: standardSchemaResolver(${groupVarPrefix}Schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
${defaultValues}
    }
  })

  const mutation = useMutation({
    mutationFn: (data: ${singular}UpsertInput) => upsert${singular}(data),
    onSuccess: async (result) => {
      const ${savedValueName} = result.${toCamelCase}
      if (${savedValueName}) {
        form.reset({
${resetValues}
        })
      }
      toast.success('${groupTitle} saved')
      void queryClient.invalidateQueries({ queryKey: ['${schemaName}'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save')
    }
  })

  const isPending = mutation.isPending
${defaultValueFromSyncFunctionsContent}
${fieldArrayHooksContent}
${creatableSelectHandlersContent}
  return (
    <Form {...form}>
      <form autoComplete="off" onSubmit={form.handleSubmit((values) => {
        mutation.mutate(values as ${singular}UpsertInput)
      })}>
        <Card >
          <CardHeader>
            <CardTitle>${groupTitle}</CardTitle>${descriptionLine}
          </CardHeader>
          <CardContent className="space-y-5">
${fieldsJsx}
          </CardContent>
          <CardFooter>${groupHintText}
            <Button
              type="submit"
              disabled={isPending || !form.formState.isDirty}
              aria-busy={isPending}
              className="ml-auto min-w-25"
            >
              {isPending && <Spinner />}
              {isPending ? 'Saving…' : 'Save'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  )
}`
}

export function renderSingleFormCardComponentRenderer(
  request:
    | {
        kind: 'renderCardComponentTabValuesTemplate'
        props: {
          groupVarPrefix: string
          map: string
          toPascalCase: string
        }
      }
    | {
        kind: 'renderCardComponentCardHeaderTemplate'
        props: {
          groupVarPrefix: string
          zodFields: string
          tabParserConst: string
          groupComponentName: string
          singular: string
          analysisHasTabsFieldTab: string
          relStateContent: string
          creatableSelectStateContent: string
          selectStateContent: string
          defaultValues: string
          savedValueName: string
          toCamelCase: string
          resetValues: string
          groupTitle: string
          schemaName: string
          defaultValueFromSyncFunctionsContent: string
          fieldArrayHooksContent: string
          creatableSelectHandlersContent: string
          descriptionLine: string
          fieldsJsx: string
          groupHintText: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderCardComponentTabValuesTemplate':
      return renderCardComponentTabValuesTemplate(request.props)
    case 'renderCardComponentCardHeaderTemplate':
      return renderCardComponentCardHeaderTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
